import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_BASE_URL from '../config';

// Async thunk for fetching document categories
export const fetchDocumentCategories = createAsyncThunk(
  'documents/fetchDocumentCategories',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/document-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Extract categories array from response
      if (response.data.success && response.data.categories) {
        return response.data.categories;
      }
      
      return [];
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch document categories';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for fetching document counts
export const fetchDocumentCounts = createAsyncThunk(
  'documents/fetchDocumentCounts',
  async (projectId = null, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = projectId 
        ? `${API_BASE_URL}/api/document-counts?projectId=${projectId}`
        : `${API_BASE_URL}/api/document-counts`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Extract counts and totals from response
      if (response.data.success) {
        return {
          counts: response.data.counts || [],
          totalsDocuments: response.data.totals || [],
          totalTestCasesGenerated: Number(response.data.totalTestCasesGenerated) || 0,
          totalDocumentsExcludingTestCases:
            Number(response.data.totalDocumentsExcludingTestCases) || 0,
          totalTestCaseDocuments: Number(response.data.totalTestCaseDocuments) || 0,
          documentsByProjectExcludingTestCases: Array.isArray(
            response.data.documentsByProjectExcludingTestCases
          )
            ? response.data.documentsByProjectExcludingTestCases
            : [],
          testCaseDocumentsByProject: Array.isArray(response.data.testCaseDocumentsByProject)
            ? response.data.testCaseDocumentsByProject
            : [],
        };
      }
      
      return {
        counts: [],
        totalsDocuments: [],
        totalTestCasesGenerated: 0,
        totalDocumentsExcludingTestCases: 0,
        totalTestCaseDocuments: 0,
        documentsByProjectExcludingTestCases: [],
        testCaseDocumentsByProject: [],
      };
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch document counts';
      return rejectWithValue(errorMsg);
    }
  }
);

const initialState = {
  // Document categories
  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  
  // Document counts
  counts: [],
  totalsDocuments: [], // legacy: completed requests per project (ProjectPage / APIs)
  totalTestCasesGenerated: 0,
  totalDocumentsExcludingTestCases: 0,
  totalTestCaseDocuments: 0,
  documentsByProjectExcludingTestCases: [],
  testCaseDocumentsByProject: [],
  countsLoading: false,
  countsError: null,
  
  // General
  success: false,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearCategoriesError: (state) => {
      state.categoriesError = null;
    },
    clearCountsError: (state) => {
      state.countsError = null;
    },
    clearAllErrors: (state) => {
      state.categoriesError = null;
      state.countsError = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    /** Per-user stats; categories stay (shared catalog). Call on logout / user switch. */
    resetUserDocumentStats: (state) => {
      state.counts = [];
      state.totalsDocuments = [];
      state.totalTestCasesGenerated = 0;
      state.totalDocumentsExcludingTestCases = 0;
      state.totalTestCaseDocuments = 0;
      state.documentsByProjectExcludingTestCases = [];
      state.testCaseDocumentsByProject = [];
      state.countsLoading = false;
      state.countsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch document categories
      .addCase(fetchDocumentCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchDocumentCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        const list = Array.isArray(action.payload) ? [...action.payload] : [];
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        state.categories = list;
        state.categoriesError = null;
      })
      .addCase(fetchDocumentCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      })
      // Fetch document counts
      .addCase(fetchDocumentCounts.pending, (state) => {
        state.countsLoading = true;
        state.countsError = null;
      })
      .addCase(fetchDocumentCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.counts = action.payload.counts;
        state.totalsDocuments = action.payload.totalsDocuments;
        state.totalTestCasesGenerated = action.payload.totalTestCasesGenerated ?? 0;
        state.totalDocumentsExcludingTestCases =
          action.payload.totalDocumentsExcludingTestCases ?? 0;
        state.totalTestCaseDocuments = action.payload.totalTestCaseDocuments ?? 0;
        state.documentsByProjectExcludingTestCases =
          action.payload.documentsByProjectExcludingTestCases ?? [];
        state.testCaseDocumentsByProject = action.payload.testCaseDocumentsByProject ?? [];
        state.countsError = null;
      })
      .addCase(fetchDocumentCounts.rejected, (state, action) => {
        state.countsLoading = false;
        state.countsError = action.payload;
      });
  },
});

export const {
  clearCategoriesError,
  clearCountsError,
  clearAllErrors,
  clearSuccess,
  resetUserDocumentStats,
} = documentsSlice.actions;

export default documentsSlice.reducer;
